import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    CircularProgress,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Popover,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardContent,
    useTheme,
    Skeleton,
    Checkbox,
    Switch,
    Grid,
    Divider
} from '@mui/material';
import { Global } from '@emotion/react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { requestsService, RightsRequestFilters, RightsRequestListItem, RightsRequestsSummary } from '../../services/requests-service';
import { useNavigate } from 'react-router-dom';
import { formatFriendlyDate } from '../../utils/dateUtils';

// Request type options for tabs
const REQUEST_TYPES = [
    { value: undefined, label: 'All Requests' },
    { value: 'CONSENT_WITHDRAWAL' as const, label: 'Consent Withdrawal' },
    { value: 'DATA_ACCESS' as const, label: 'Data Access' },
    { value: 'DATA_CORRECTION' as const, label: 'Data Correction' },
    { value: 'DATA_ERASURE' as const, label: 'Data Erasure' },
    { value: 'DATA_PORTABILITY' as const, label: 'Data Portability' }
];

// Status color mapping - theme-aware
const getStatusColor = (status: string, isDark: boolean) => {
    if (isDark) {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
            case 'IN_PROGRESS':
                return { bg: '#003D47', color: '#4DD0E1', border: '#17A2B8' };
            case 'COMPLETED':
                return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
            case 'REJECTED':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            case 'CANCELLED':
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
            case 'IN_PROGRESS':
                return { bg: '#D1ECF1', color: '#0C5460', border: '#17A2B8' };
            case 'COMPLETED':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            case 'REJECTED':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            case 'CANCELLED':
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

// Priority color mapping - theme-aware
const getPriorityColor = (priority: string, isDark: boolean) => {
    if (isDark) {
        switch (priority.toUpperCase()) {
            case 'LOW':
                return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
            case 'NORMAL':
                return { bg: '#003D47', color: '#4DD0E1', border: '#17A2B8' };
            case 'HIGH':
                return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
            case 'URGENT':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (priority.toUpperCase()) {
            case 'LOW':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            case 'NORMAL':
                return { bg: '#D1ECF1', color: '#0C5460', border: '#17A2B8' };
            case 'HIGH':
                return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
            case 'URGENT':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

// Request type label mapping
const getRequestTypeLabel = (type: string) => {
    const found = REQUEST_TYPES.find(t => t.value === type);
    return found ? found.label : type.replace(/_/g, ' ');
};

const Requests: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    // State management
    const [requests, setRequests] = useState<RightsRequestListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Summary state
    const [summary, setSummary] = useState<RightsRequestsSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    // Pagination state
    const [page, setPage] = useState(0); // Material-UI uses 0-based indexing
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    // Filter state
    const [filters, setFilters] = useState<RightsRequestFilters>({
        page: 1,
        pageSize: 20
    });

    // Active filter card tracking (which card is selected)
    const [activeCard, setActiveCard] = useState<'total' | 'urgent' | 'open' | 'closed' | null>('total'); // Default to 'total'

    // Filter UI state
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [tempFilters, setTempFilters] = useState<RightsRequestFilters>({
        page: 1,
        pageSize: 20
    });
    const [filterLoading, setFilterLoading] = useState(false);

    // Load summary on mount and when filters change
    useEffect(() => {
        loadSummary();
    }, []);

    // Note: Summary is refreshed on mount and when user manually refreshes
    // We don't auto-refresh on filter changes to avoid unnecessary API calls

    // Data fetching effect
    useEffect(() => {
        loadRequests();
    }, [
        page,
        pageSize,
        filters.requestType,
        filters.requestTypes,
        filters.requestStatus,
        filters.requestStatuses,
        filters.isUrgent,
        filters.priority,
        filters.assignedTo,
        filters.startDate,
        filters.endDate,
        filters.search
    ]);

    const loadSummary = async () => {
        try {
            setSummaryLoading(true);
            const response = await requestsService.getRequestsSummary();
            if (response.success && response.data) {
                setSummary(response.data);
            } else {
                // Set default values if API fails
                setSummary({
                    total: 0,
                    urgent: 0,
                    open: 0,
                    closed: 0
                });
            }
        } catch (error) {
            console.error('Error loading summary:', error);
            // Set default values on error
            setSummary({
                total: 0,
                urgent: 0,
                open: 0,
                closed: 0
            });
        } finally {
            setSummaryLoading(false);
        }
    };

    const loadRequests = async () => {
        try {
            setLoading(true);
            setError(null);

            const requestFilters: RightsRequestFilters = {
                ...filters,
                page: page + 1, // Convert 0-based to 1-based for API
                pageSize: pageSize
            };

            const response = await requestsService.listRequests(requestFilters);

            if (response.success && response.data) {
                setRequests(response.data.requests);
                setTotalCount(response.data.totalCount);
                setTotalPages(response.data.totalPages);
                setHasNextPage(response.data.hasNextPage);
                setHasPrevPage(response.data.hasPrevPage);
            } else {
                setError(response.error || 'Failed to fetch requests');
            }
        } catch (error) {
            console.error('Error loading requests:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch requests');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setPage(0); // Reset to first page
        setFilters(prev => ({ ...prev, page: 1, pageSize: newPageSize }));
    };

    // Card click handler - applies filters directly
    const handleCardClick = (cardType: 'total' | 'urgent' | 'open' | 'closed') => {
        const newFilters: RightsRequestFilters = {
            page: 1,
            pageSize: 20
        };

        if (cardType === 'urgent') {
            // Urgent: isUrgent flag
            newFilters.isUrgent = true;
        } else if (cardType === 'open') {
            // Open: PENDING + IN_PROGRESS + REJECTED
            newFilters.requestStatuses = ['PENDING', 'IN_PROGRESS', 'REJECTED'];
        } else if (cardType === 'closed') {
            // Closed: COMPLETED + CANCELLED
            newFilters.requestStatuses = ['COMPLETED', 'CANCELLED'];
        }
        // 'total' - no filters (show all)

        setFilters(newFilters);
        setTempFilters({ ...newFilters }); // Copy for filter popup
        setActiveCard(cardType);
        setPage(0);
    };

    // Filter UI handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        setTempFilters({ ...filters });
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleTempFilterChange = (field: keyof RightsRequestFilters, value: any) => {
        setTempFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        try {
            setFilterLoading(true);
            setFilters({ ...tempFilters, page: 1 });
            setPage(0);
            handleFilterClose();
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setFilterLoading(false);
        }
    };

    const handleClearFilters = () => {
        const clearedFilters: RightsRequestFilters = {
            page: 1,
            pageSize: 20
        };
        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
        setActiveCard('total'); // Reset to total card
        setPage(0);
    };

    const removeFilter = (key: keyof RightsRequestFilters) => {
        const newFilters = { ...filters, [key]: undefined, page: 1 };
        setFilters(newFilters);
        setTempFilters(prev => ({ ...prev, [key]: undefined }));
        // Don't reset activeCard - let user decide which card to click
        setPage(0);
    };

    const hasActiveFilters = () => {
        return !!(
            filters.requestStatuses ||
            filters.requestStatus ||
            filters.requestTypes ||
            filters.requestType ||
            filters.isUrgent ||
            filters.priority ||
            filters.assignedTo ||
            filters.startDate ||
            filters.endDate ||
            filters.search ||
            (activeCard && activeCard !== 'total')
        );
    };

    const handleRequestClick = (requestId: number) => {
        navigate(`/workbench/requests/${requestId}`);
    };

    if (loading && requests.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && requests.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Requests
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={() => {
                        loadRequests();
                        loadSummary();
                    }} sx={{ mt: 2 }}>
                        Retry
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <AssignmentIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                            DP Rights Requests
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage data principal rights requests including consent withdrawal, data access, correction, erasure, and portability
                        </Typography>
                    </Box>
                </Box>

                {/* Statistics Ribbon */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Overview
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            pb: 2,
                            px: 1,
                            '&::-webkit-scrollbar': {
                                height: 8,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                borderRadius: 4,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                                borderRadius: 4,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
                                },
                            },
                        }}
                    >
                        {summaryLoading ? (
                            // Loading skeletons - 4 cards
                            Array.from({ length: 4 }).map((_, index) => (
                                <Skeleton
                                    key={index}
                                    variant="rectangular"
                                    width={160}
                                    height={100}
                                    sx={{ borderRadius: 2, flexShrink: 0 }}
                                />
                            ))
                        ) : (
                            <>
                                {/* Total Requests Card */}
                                <Card
                                    sx={{
                                        minWidth: 160,
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        border: activeCard === 'total'
                                            ? `2px solid ${theme.palette.primary.main}`
                                            : `1px solid ${theme.palette.divider}`,
                                        bgcolor: activeCard === 'total'
                                            ? theme.palette.mode === 'dark'
                                                ? 'rgba(163, 205, 50, 0.15)'
                                                : 'rgba(163, 205, 50, 0.08)'
                                            : 'background.paper',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.shadows[4],
                                        },
                                    }}
                                    onClick={() => handleCardClick('total')}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {summary?.total ?? 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Requests
                                        </Typography>
                                    </CardContent>
                                </Card>

                                {/* Urgent Card */}
                                <Card
                                    sx={{
                                        minWidth: 160,
                                        flexShrink: 0,
                                        cursor: (summary?.urgent ?? 0) > 0 ? 'pointer' : 'default',
                                        transition: 'all 0.2s ease-in-out',
                                        border: activeCard === 'urgent'
                                            ? `2px solid ${theme.palette.error.main}`
                                            : (summary?.urgent ?? 0) > 0
                                            ? `2px solid ${theme.palette.error.main}`
                                            : `1px solid ${theme.palette.divider}`,
                                        bgcolor: activeCard === 'urgent'
                                            ? theme.palette.mode === 'dark'
                                                ? 'rgba(211, 47, 47, 0.2)'
                                                : 'rgba(211, 47, 47, 0.1)'
                                            : (summary?.urgent ?? 0) > 0
                                            ? theme.palette.mode === 'dark'
                                                ? 'rgba(211, 47, 47, 0.15)'
                                                : 'rgba(255, 235, 238, 0.5)'
                                            : 'background.paper',
                                        opacity: (summary?.urgent ?? 0) > 0 ? 1 : 0.6,
                                        '&:hover': (summary?.urgent ?? 0) > 0 ? {
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.shadows[4],
                                        } : {},
                                    }}
                                    onClick={() => (summary?.urgent ?? 0) > 0 && handleCardClick('urgent')}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'error.main' }}>
                                                {summary?.urgent ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Urgent
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Open Requests Card */}
                                <Card
                                    sx={{
                                        minWidth: 160,
                                        flexShrink: 0,
                                        cursor: (summary?.open ?? 0) > 0 ? 'pointer' : 'default',
                                        transition: 'all 0.2s ease-in-out',
                                        border: activeCard === 'open'
                                            ? `2px solid ${theme.palette.warning.main}`
                                            : `1px solid ${theme.palette.divider}`,
                                        bgcolor: activeCard === 'open'
                                            ? theme.palette.mode === 'dark'
                                                ? 'rgba(255, 152, 0, 0.2)'
                                                : 'rgba(255, 152, 0, 0.1)'
                                            : 'background.paper',
                                        opacity: (summary?.open ?? 0) > 0 ? 1 : 0.6,
                                        '&:hover': (summary?.open ?? 0) > 0 ? {
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.shadows[4],
                                        } : {},
                                    }}
                                    onClick={() => (summary?.open ?? 0) > 0 && handleCardClick('open')}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.main' }}>
                                            {summary?.open ?? 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Open Requests
                                        </Typography>
                                    </CardContent>
                                </Card>

                                {/* Closed Requests Card */}
                                <Card
                                    sx={{
                                        minWidth: 160,
                                        flexShrink: 0,
                                        cursor: (summary?.closed ?? 0) > 0 ? 'pointer' : 'default',
                                        transition: 'all 0.2s ease-in-out',
                                        border: activeCard === 'closed'
                                            ? `2px solid ${theme.palette.success.main}`
                                            : `1px solid ${theme.palette.divider}`,
                                        bgcolor: activeCard === 'closed'
                                            ? theme.palette.mode === 'dark'
                                                ? 'rgba(76, 175, 80, 0.2)'
                                                : 'rgba(76, 175, 80, 0.1)'
                                            : 'background.paper',
                                        opacity: (summary?.closed ?? 0) > 0 ? 1 : 0.6,
                                        '&:hover': (summary?.closed ?? 0) > 0 ? {
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.shadows[4],
                                        } : {},
                                    }}
                                    onClick={() => (summary?.closed ?? 0) > 0 && handleCardClick('closed')}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'success.main' }}>
                                            {summary?.closed ?? 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Closed Requests
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Controls */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Active Filters Display */}
                        {hasActiveFilters() && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                {filters.requestStatuses && filters.requestStatuses.length > 0 && (
                                    <Chip
                                        label={`Status: ${filters.requestStatuses.join(', ')}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onDelete={() => removeFilter('requestStatuses')}
                                    />
                                )}
                                {filters.requestStatus && !filters.requestStatuses && (
                                    <Chip
                                        label={`Status: ${filters.requestStatus}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onDelete={() => removeFilter('requestStatus')}
                                    />
                                )}
                                {filters.requestTypes && filters.requestTypes.length > 0 && (
                                    <Chip
                                        label={`Type: ${filters.requestTypes.map(t => getRequestTypeLabel(t)).join(', ')}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onDelete={() => removeFilter('requestTypes')}
                                    />
                                )}
                                {filters.requestType && !filters.requestTypes && (
                                    <Chip
                                        label={`Type: ${getRequestTypeLabel(filters.requestType)}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onDelete={() => removeFilter('requestType')}
                                    />
                                )}
                                {filters.isUrgent && (
                                    <Chip
                                        label="Urgent"
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onDelete={() => removeFilter('isUrgent')}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        ml: 'auto'
                    }}>
                        <IconButton
                            onClick={() => {
                                loadRequests();
                                loadSummary();
                            }}
                            sx={{ color: theme.palette.text.primary }}
                            disabled={loading || summaryLoading}
                        >
                            <RefreshIcon
                                sx={{
                                    transition: 'transform 0.3s',
                                    animation: (loading || summaryLoading) ? 'spin 1s linear infinite' : undefined
                                }}
                            />
                        </IconButton>
                        <IconButton
                            onClick={handleFilterClick}
                            sx={{
                                color: hasActiveFilters() ? theme.palette.primary.main : theme.palette.text.primary,
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                },
                                position: 'relative',
                                '&::after': hasActiveFilters() ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.main,
                                    border: `2px solid ${theme.palette.background.paper}`,
                                } : {}
                            }}
                        >
                            <FilterListIcon />
                        </IconButton>
                        {hasActiveFilters() && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClearFilters}
                                sx={{
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        borderColor: 'primary.dark',
                                    }
                                }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* CSS Animation for Refresh Icon */}
                <Global styles={`
                    @keyframes spin {
                        100% { transform: rotate(360deg); }
                    }
                `} />

                {/* Filter Popover */}
                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            width: 600,
                            bgcolor: theme.palette.background.paper,
                            boxShadow: theme.shadows[8],
                        },
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Filters
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={3}>
                            {/* Left Column */}
                            <Grid item xs={6}>
                                {/* Status Checkboxes */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                                        Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'CANCELLED'].map((status) => {
                                            const selectedStatuses = tempFilters.requestStatuses || [];
                                            const isChecked = selectedStatuses.includes(status as any);
                                            return (
                                                <FormControlLabel
                                                    key={status}
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                const currentStatuses = tempFilters.requestStatuses || [];
                                                                let newStatuses: string[];
                                                                if (e.target.checked) {
                                                                    newStatuses = [...currentStatuses, status];
                                                                } else {
                                                                    newStatuses = currentStatuses.filter(s => s !== status);
                                                                }
                                                                setTempFilters(prev => ({
                                                                    ...prev,
                                                                    requestStatuses: newStatuses.length > 0 ? newStatuses as any : undefined,
                                                                    requestStatus: undefined
                                                                }));
                                                            }}
                                                            size="small"
                                                            sx={{ color: theme.palette.text.secondary }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                            {status.replace(/_/g, ' ')}
                                                        </Typography>
                                                    }
                                                />
                                            );
                                        })}
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            const allStatuses: Array<'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED'> = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'CANCELLED'];
                                            const selectedStatuses = tempFilters.requestStatuses || [];
                                            const allSelected = allStatuses.every(s => selectedStatuses.includes(s));
                                            setTempFilters(prev => ({
                                                ...prev,
                                                requestStatuses: allSelected ? undefined : allStatuses,
                                                requestStatus: undefined
                                            }));
                                        }}
                                        sx={{ mt: 1, fontSize: '0.75rem', color: theme.palette.primary.main }}
                                    >
                                        {(() => {
                                            const allStatuses: Array<'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED'> = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'CANCELLED'];
                                            const selectedStatuses = tempFilters.requestStatuses || [];
                                            const allSelected = allStatuses.every(s => selectedStatuses.includes(s));
                                            return allSelected ? 'Deselect All' : 'Select All';
                                        })()}
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Right Column */}
                            <Grid item xs={6}>
                                {/* Request Type Checkboxes */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                                        Request Type
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {['CONSENT_WITHDRAWAL', 'DATA_ACCESS', 'DATA_CORRECTION', 'DATA_ERASURE', 'DATA_PORTABILITY'].map((type) => {
                                            const selectedTypes = tempFilters.requestTypes || [];
                                            const isChecked = selectedTypes.includes(type as any);
                                            return (
                                                <FormControlLabel
                                                    key={type}
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                const currentTypes = tempFilters.requestTypes || [];
                                                                let newTypes: string[];
                                                                if (e.target.checked) {
                                                                    newTypes = [...currentTypes, type];
                                                                } else {
                                                                    newTypes = currentTypes.filter(t => t !== type);
                                                                }
                                                                setTempFilters(prev => ({
                                                                    ...prev,
                                                                    requestTypes: newTypes.length > 0 ? newTypes as any : undefined,
                                                                    requestType: undefined
                                                                }));
                                                            }}
                                                            size="small"
                                                            sx={{ color: theme.palette.text.secondary }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                            {getRequestTypeLabel(type)}
                                                        </Typography>
                                                    }
                                                />
                                            );
                                        })}
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            const allTypes: Array<'CONSENT_WITHDRAWAL' | 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY'> = ['CONSENT_WITHDRAWAL', 'DATA_ACCESS', 'DATA_CORRECTION', 'DATA_ERASURE', 'DATA_PORTABILITY'];
                                            const selectedTypes = tempFilters.requestTypes || [];
                                            const allSelected = allTypes.every(t => selectedTypes.includes(t));
                                            setTempFilters(prev => ({
                                                ...prev,
                                                requestTypes: allSelected ? undefined : allTypes,
                                                requestType: undefined
                                            }));
                                        }}
                                        sx={{ mt: 1, fontSize: '0.75rem', color: theme.palette.primary.main }}
                                    >
                                        {(() => {
                                            const allTypes: Array<'CONSENT_WITHDRAWAL' | 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY'> = ['CONSENT_WITHDRAWAL', 'DATA_ACCESS', 'DATA_CORRECTION', 'DATA_ERASURE', 'DATA_PORTABILITY'];
                                            const selectedTypes = tempFilters.requestTypes || [];
                                            const allSelected = allTypes.every(t => selectedTypes.includes(t));
                                            return allSelected ? 'Deselect All' : 'Select All';
                                        })()}
                                    </Button>
                                </Box>

                                {/* Urgent Toggle */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                                        Urgent
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={tempFilters.isUrgent || false}
                                                onChange={(e) => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        isUrgent: e.target.checked || undefined
                                                    }));
                                                }}
                                                size="small"
                                                sx={{ color: theme.palette.text.secondary }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                Show only urgent requests
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>


                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={handleFilterClose}
                                disabled={filterLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={filterLoading}
                            >
                                {filterLoading ? <CircularProgress size={20} /> : 'Apply'}
                            </Button>
                        </Box>
                    </Box>
                </Popover>

                {/* Requests Table */}
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Request ID</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Priority</strong></TableCell>
                                    <TableCell><strong>Purpose</strong></TableCell>
                                    <TableCell><strong>DP ID</strong></TableCell>
                                    <TableCell><strong>Identifier Token</strong></TableCell>
                                    <TableCell><strong>Assigned To</strong></TableCell>
                                    <TableCell><strong>Created Date</strong></TableCell>
                                    <TableCell><strong>Estimated Completion</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && requests.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : requests.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No requests found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    requests.map((request) => {
                                        const isDark = theme.palette.mode === 'dark';
                                        const statusColors = getStatusColor(request.requestStatus, isDark);
                                        const priorityColors = getPriorityColor(request.priority, isDark);
                                        return (
                                            <TableRow
                                                key={request.requestId}
                                                hover
                                                onClick={() => handleRequestClick(request.requestId)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: theme.palette.action.hover,
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        #{request.requestId}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {getRequestTypeLabel(request.requestType)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={request.requestStatus}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: statusColors.bg,
                                                            color: statusColors.color,
                                                            border: `1px solid ${statusColors.border}`,
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={request.priority}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: priorityColors.bg,
                                                            color: priorityColors.color,
                                                            border: `1px solid ${priorityColors.border}`,
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {request.purposeName || 'N/A'}
                                                    </Typography>
                                                    {request.purposeKey && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {request.purposeKey}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                                        #{request.dpId}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                                    >
                                                        {request.dpIdentifierToken || request.dpIdentifier || 'Unavailable'}
                                                    </Typography>
                                                    {request.dpIdentifierType && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {request.dpIdentifierToken ? request.dpIdentifierType : `TEST ${request.dpIdentifierType}`}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {request.assignedToName || 'Unassigned'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatFriendlyDate(request.createdDate)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {request.estimatedCompletionDate
                                                            ? formatFriendlyDate(request.estimatedCompletionDate)
                                                            : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handlePageSizeChange}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default Requests;

